import { Puzzle } from '../shared/types';
import { CellState } from '../shared/board';
import { RuleId, RuleInstance, enumerateInstances } from '../shared/rules';
import { Hint, HintRule, computeHints, present } from './hints';
import { TRAINABLE_RULES } from './editor';

// The catalog behind the Tips & Tricks page: one entry per human-usable rule in the hints engine,
// each with prose a player can act on and three worked examples.
//
// The examples are *not* drawn by hand. Each one names a real puzzle (a unique-solution board),
// the marks a player would already have made, and a predicate picking one of the deductions the
// engine finds there; `resolveExample` runs the real rule engine and hands the resulting
// highlight to the same `Board` the game uses. So the picture cannot drift from the rule: if a
// rule changes, the example either changes with it or the page fails to resolve — and
// techniques.test.ts pins both (the intended rule fires, and its verdict is sound against every
// solution consistent with the marks shown).
//
// The boards themselves came out of `scripts/_find-examples.ts` (search random puzzles for a
// position where the wanted technique fires, then prune the Xs down to the ones the deduction
// needs) and `scripts/_recolor-examples.ts` (relabel each board so regions that touch are as far
// apart in the palette as possible — blue against purple is unreadable at board size).

export interface TechniqueExample {
  /** Color regions, one string per row, one letter per cell. Always a unique-solution puzzle. */
  rows: string[];
  /** Queens already placed. */
  queens?: string[];
  /** Squares already crossed off. Every one is true of the puzzle's real solution. */
  xs?: string[];
  /**
   * Which of the deductions available on that board to illustrate. Omitted for R1, which is a
   * view concern (`computeHints` synthesizes it) rather than a rule instance.
   */
  pick?: (i: RuleInstance) => boolean;
  /** What the picture shows, in plain words — read alongside the board. */
  caption: string;
}

export interface Technique {
  rule: HintRule;
  /** Display name. Matches RULE_NAMES for the rules that have one. */
  name: string;
  /** The rule in one sentence — the thing to remember. */
  tagline: string;
  /** How to actually use it, in paragraphs. */
  body: string[];
  /** Worked examples, browsable on the page. Three each: one rule, three shapes it takes. */
  examples: TechniqueExample[];
}

/**
 * Pick one deduction out of everything available on a board: its rule, its shape, and squares it
 * must rule out. Precise enough that the picture can't quietly become a different deduction, and
 * checked by the test — an example that no longer resolves is a loud failure, not a blank board.
 */
const finds = (rule: RuleId, variant: string, ...cells: string[]) => (i: RuleInstance) =>
  i.sig.rule === rule && i.sig.variant === variant && cells.every(k => i.cells.includes(k));

/**
 * Ordered as a course, not by rule number: the bookkeeping move every board needs first, then
 * the single-unit eliminations from most to least common, then the two pigeonholes, then the
 * pairwise rule that only matters once the rest have run out.
 */
export const TECHNIQUES: Technique[] = [
  {
    rule: 'R1',
    name: "Queen's reach",
    tagline: 'A queen rules out its row, its column, the squares touching it, and the rest of its color.',
    body: [
      'Cross off the queen’s row, column, diagonal neighbours, and every other square of its color. You can enable Auto Xs to mark these automatically.'
    ],
    examples: [
      {
        rows: ['FFBBB', 'EFAAB', 'EEAAB', 'CEEAA', 'CCCCC'],
        queens: ['2,4'],
        caption: 'Out go the row, the column and the neighbours — plus the two blue squares up in the top row.',
      },
      {
        rows: ['CABFF', 'CAAAF', 'CEAAF', 'CEEAA', 'CCEEA'],
        queens: ['2,1'],
        caption: 'The far end of purple’s staircase, down in the bottom row, goes on color alone.',
      },
      {
        rows: ['FFCCC', 'FDBBC', 'FDABC', 'FDAAA', 'FFFFA'],
        queens: ['0,2'],
        caption: 'The two easy to miss: the green squares down the right-hand edge, out on color.',
      },
    ],
  },
  {
    rule: 'R7',
    name: 'Region shadow',
    tagline: "Any square attacked by every square a color could still use can't hold a queen.",
    body: [
      'Look at the squares a color has left. Any square attacked by all of them is dead.',
      'Small regions work best. Two squares side by side kill the ones flanking them; three in a row clear above and below the middle; an L of three kills the square completing its block plus the two extending its arms. A solid two-by-two casts no shadow.',
    ],
    examples: [
      {
        rows: ['CCBBF', 'CDBBF', 'CDDBF', 'CABBF', 'CABFF'],
        pick: finds('R7', 'adj2', '3,0', '3,2', '4,0', '4,2'),
        caption: 'Red is two squares stacked, so all four squares flanking the pair go before anything is placed.',
      },
      {
        rows: ['ACCCB', 'ADDDB', 'AAABB', 'FAAAF', 'FFFFF'],
        pick: finds('R7', 'line', '0,2', '2,2'),
        caption: 'Three orange squares in a row. Only the squares directly above and below the middle one go.',
      },
      {
        rows: ['BAAAA', 'BAAAA', 'BFACC', 'FFEEC', 'FFECC'],
        pick: finds('R7', 'Lshape', '3,1', '2,2', '4,3'),
        caption: 'Purple’s L kills the square completing its two-by-two block, plus the two extending its arms.',
      },
    ],
  },
  {
    rule: 'R3',
    name: 'Color locked to a line',
    tagline: 'A color whose squares all lie in one row owns that row.',
    body: [
      'If all that a color has left sits in one row, the rest of that row cannot have a queen.',
      'Look for strip-shaped regions, and wider ones the Xs have squeezed into a line. Re-check after every batch of Xs.',
    ],
    examples: [
      {
        rows: ['CCAAB', 'EAAAB', 'EABBB', 'EAAFB', 'FFFFB'],
        pick: finds('R3', 'row', '0,2', '0,3', '0,4'),
        caption: 'Green is two squares in the top row, so the row is green’s and the other three go.',
      },
      {
        rows: ['EEEEE', 'ECAEF', 'ACAAF', 'ACABB', 'AAAAA'],
        pick: finds('R3', 'col', '0,4', '3,4', '4,4'),
        caption: 'Standing up: yellow is a strip in the right-hand column, so that column’s three other squares are out.',
      },
      {
        rows: ['CCBBB', 'CDBAB', 'CDDAF', 'AAAAF', 'FFFFF'],
        xs: ['0,2', '1,0', '2,0'],
        pick: finds('R3', 'row', '0,3', '0,4'),
        caption: 'The Xs did it: green is down to the top row, so the far end of that row goes.',
      },
    ],
  },
  {
    rule: 'R4',
    name: 'Line locked to a color',
    tagline: 'A row whose remaining squares are all one color pins that color to the row.',
    body: [
      'If a row has only one color still available in it, that color is used up there, so every other square of that color is dead.',
    ],
    examples: [
      {
        rows: ['AAAAA', 'ADDDA', 'ADBDC', 'ABBCC', 'FFBCC'],
        pick: finds('R4', 'row', '1,0', '1,4', '2,0', '3,0'),
        caption: 'The top row is all red, so red is finished — its squares down the left-hand side and at the end of the second row go.',
      },
      {
        rows: ['FBBBB', 'FAAAB', 'FECAB', 'EECAB', 'ECCAB'],
        pick: finds('R4', 'col', '0,1', '0,2', '0,3'),
        caption: 'The right-hand column is all blue, so the three blue squares along the top row go — right across the board.',
      },
      {
        rows: ['CAAAB', 'CAAFB', 'CCEFF', 'CEEFE', 'CCEEE'],
        xs: ['0,3', '1,0', '1,3', '1,4'],
        pick: finds('R4', 'row', '0,1', '0,2'),
        caption: 'The second row has only red left, so the two red squares directly above it go.',
      },
    ],
  },
  {
    rule: 'R2',
    name: 'Line squeeze',
    tagline: 'Wherever a row\'s queen ends up, it attacks the same few squares — those are out.',
    body: [
      'The same shadow argument, on a row or a column. Once a row is down to two or three candidates, see what all of them attack — usually the squares just above and below.',
      'It fires late, so try it when a board full of Xs has stalled.',
    ],
    examples: [
      {
        rows: ['ACCCC', 'AADDC', 'AADDC', 'FABCC', 'FFBBB'],
        xs: ['0,0', '0,1', '0,2'],
        pick: finds('R2', 'row', '1,3', '1,4'),
        caption: 'The top row is down to two squares side by side, so both squares beneath the pair go.',
      },
      {
        rows: ['AACCC', 'AAAAA', 'EEEFA', 'FFFFB', 'FFFBB'],
        xs: ['0,1', '1,1', '3,1'],
        pick: finds('R2', 'col', '3,0', '3,2'),
        caption: 'Two squares left in the second column, with a gap: the squares either side of the gap go.',
      },
      {
        rows: ['CCAAEE', 'CAAEEE', 'BAAAEF', 'BBFFEF', 'DBBFFF', 'DDBBBB'],
        xs: ['2,0', '2,1', '2,5'],
        pick: finds('R2', 'row', '1,3', '3,3'),
        caption: 'A run of three in the middle row kills just two squares: directly above and below its middle.',
      },
    ],
  },
  {
    rule: 'R5',
    name: 'Pigeonhole (colors)',
    tagline: 'Two colors that fit inside two rows fill those rows — nothing else may go there.',
    body: [
      'Two colors, two rows, two queens: if both colors keep everything inside the same two rows, those rows are taken and any third-color square in them is dead.',
      'Any count works. The skill is spotting the pair: a color squeezed into a band of rows, and a second trapped in the same band.',
    ],
    examples: [
      {
        rows: ['BBACE', 'BBACE', 'FAAAE', 'FAAAF', 'FFFFF'],
        pick: finds('R5', 'rows', '0,2', '0,4', '1,2', '1,4'),
        caption: 'Blue and green are boxed into the top two rows, so the red and purple squares there are out.',
      },
      {
        rows: ['BBFFF', 'BBBAF', 'BDDAA', 'BDCAA', 'BCCAA'],
        pick: finds('R5', 'cols', '0,1', '1,1', '0,2', '1,2'),
        caption: 'The same on columns: orange and green own the second and third, so the blue and yellow squares there go.',
      },
      {
        rows: ['DDDDD', 'AEEED', 'AECDD', 'ACCCD', 'BBBDD'],
        pick: finds('R5', 'rows', '1,4', '2,3', '2,4', '3,4'),
        caption: 'Any count works: red, purple and green fill the middle three rows, so orange squares reaching in are out.',
      },
    ],
  },
  {
    rule: 'R6',
    name: 'Pigeonhole (lines)',
    tagline: 'Two rows that contain only two colors use those colors up.',
    body: [
      'The dual, read from the other side. If two rows offer only two colors between them, those colors supply both queens, so every square of either one outside those rows is out.',
      'Look at edge rows and columns: a large corner region can cover most of two lines by itself.',
    ],
    examples: [
      {
        rows: ['FFFBB', 'FFBBB', 'FAABB', 'EEAAA', 'EECCC'],
        pick: finds('R6', 'rows', '2,0', '2,3', '2,4'),
        caption: 'The top two rows offer only yellow and blue, so both are done and their squares in the row below go.',
      },
      {
        rows: ['CCCCC', 'CCAAA', 'EEABB', 'EEABF', 'EEFFF'],
        pick: finds('R6', 'cols', '0,2', '0,3', '0,4'),
        caption: 'The left-hand two columns hold only green and purple, so the rest of the green strip along the top is out.',
      },
      {
        rows: ['CEEEE', 'CAEEE', 'CAEEE', 'AAAFF', 'BBBFF'],
        pick: finds('R6', 'rows', '3,0', '3,1', '3,2'),
        caption: 'Three rows: the top three offer only green, red and purple, so the red squares in the fourth row go.',
      },
    ],
  },
  {
    rule: 'R9',
    name: 'Paired shadow',
    tagline: 'Two units that each need a queen, and cannot attack each other, cast a joint shadow.',
    body: [
      'The advanced one, and why hard puzzles are hard. Two units that each still need a queen get queens that cannot attack each other, so some pairings of their candidates are impossible; the survivors can agree on a square neither unit rules out alone.',
      'On a stalled board, pick two nearby units, try each pairing, and drop the ones where the queens would touch. A square hit in every survivor is dead.',
    ],
    examples: [
      {
        rows: ['CCBBB', 'CDBAB', 'CDDAF', 'AAAAF', 'FFFFF'],
        xs: ['1,1', '1,2', '1,3', '2,0', '2,3', '2,4', '3,1', '3,2', '3,4'],
        pick: finds('R9', 'colorxcolor', '4,1', '1,0', '4,2'),
        caption: 'Orange and red have two squares each, but their left pair would touch, and so would their right. Only the crossed pairings survive, covering three squares neither region kills alone.',
      },
      {
        rows: ['CCAAA', 'CAAFE', 'AABFE', 'AABFF', 'ABBFF'],
        xs: ['0,3', '0,4', '1,1', '1,3', '2,1', '2,3', '3,1', '3,2', '3,4', '4,2', '4,4'],
        pick: finds('R9', 'colorxrow', '1,0', '1,2'),
        caption: 'A color against a line: purple’s two squares in the right-hand column against the fourth row’s two. Every pairing that survives hits both marked squares.',
      },
      {
        rows: ['CCCCC', 'CBDCA', 'BBDDA', 'FBBAA', 'FFBAA'],
        xs: ['0,1', '0,2', '0,3', '1,0', '1,3', '1,4', '2,1', '2,2', '3,1', '3,2', '3,4'],
        pick: finds('R9', 'rowxcol', '4,2', '4,4'),
        caption: 'Two lines pair up as well: the top row and the second column have two squares each, and every pairing but the touching corner one attacks the two marked squares.',
      },
    ],
  },
];

/** The practice target the puzzle generator offers for this rule, if it offers one. */
export function practiceShare(rule: HintRule): number | undefined {
  return TRAINABLE_RULES.find(t => t.rule === rule)?.minShare;
}

export interface ResolvedExample {
  puzzle: Puzzle;
  board: CellState[][];
  hint: Hint;
}

/**
 * Run the real engine on an example board and return the deduction it illustrates, ready for
 * `Board`'s highlight. Throws if the rule no longer fires there — a loud failure in a test beats
 * a page quietly showing an unhighlighted board.
 */
export function resolveExample(t: Technique, ex: TechniqueExample): ResolvedExample {
  const { rows, queens = [], xs = [], pick } = ex;
  const colorRegions = rows.map(r => r.split(''));
  const size = colorRegions.length;
  const board: CellState[][] = Array.from({ length: size }, () => Array(size).fill(null));
  for (const k of queens) { const [r, c] = parseKey(k); board[r][c] = 'queen'; }
  for (const k of xs) { const [r, c] = parseKey(k); board[r][c] = 'X'; }

  const hint = pick
    ? present(pickInstance(enumerateInstances(board, colorRegions), pick, t))
    : computeHints(board, colorRegions).find(h => h.rule === t.rule);
  if (!hint) throw new Error(`No ${t.rule} hint on a ${t.name} example board`);

  return {
    puzzle: { id: -1, name: t.name, size, colorRegions },
    board,
    hint,
  };
}

function pickInstance(all: RuleInstance[], pick: (i: RuleInstance) => boolean, t: Technique): RuleInstance {
  const hit = all.find(pick);
  if (!hit) throw new Error(`No ${t.rule} instance on a ${t.name} example board`);
  return hit;
}

function parseKey(k: string): [number, number] {
  const [r, c] = k.split(',');
  return [Number(r), Number(c)];
}
